import * as DialogPrimitive from "@radix-ui/react-dialog"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import clsx from "clsx"
import { ComponentPropsWithoutRef, createContext, useCallback, useContext, useEffect, useMemo } from "react"

import useControllableOpenState from "@hooks/useControllableOpenState"
import useIsAnyDialogOpen from "@hooks/useIsAnyDialogOpen"
import useIsDesktop from "@hooks/useIsDesktop"
import useLayerStack, { LAYER_Z_INDEX } from "@hooks/useLayerStack"

import BaseOverlay from "@particles/Overlay/Overlay"

import { BottomDrawer } from "@atoms"

import { ComponentNavigation } from "@organisms"

import styles from "./Popover.module.scss"
import {
    PopoverContextValues,
    PopoverProps,
    PopoverViewportNavigationProps,
    PopoverViewportProps,
} from "./Popover.types"
import PopoverBody from "./PopoverBody/PopoverBody"
import PopoverButton from "./PopoverButton/PopoverButton"
import PopoverFooter from "./PopoverFooter/PopoverFooter"
import PopoverSection from "./PopoverSection/PopoverSection"
import PopoverSurfaceHeader from "./PopoverSurfaceHeader/PopoverSurfaceHeader"
import PopoverView from "./PopoverView/PopoverView"

const BASE_Z = LAYER_Z_INDEX.BASE + LAYER_Z_INDEX.OVERLAY_OFFSET

export const PopoverContext = createContext<PopoverContextValues>({
    isOpen: false,
    isAnchored: true,
    isDismissable: true,
    hasDragIndicator: true,
    mobilePresentation: "drawer",
    setIsOpen: () => {},
    overlayZIndex: BASE_Z,
    surfaceZIndex: BASE_Z + LAYER_Z_INDEX.SURFACE_OFFSET,
    floatingZIndex: BASE_Z + LAYER_Z_INDEX.FLOATING_OFFSET,
})
PopoverContext.displayName = "Popover"

function Popover(props: PopoverProps) {
    const {
        isOpen: controlledOpen,
        onOpenChange,
        isAnchored = true,
        isDismissable = true,
        hasDragIndicator = isDismissable,
        mobilePresentation: mobilePresentationProp,
        children,
    } = props
    // mobilePresentation only takes effect when anchored; falls back to "drawer".
    const mobilePresentation = isAnchored ? (mobilePresentationProp ?? "drawer") : "drawer"

    const { setIsAnyDialogOpen } = useIsAnyDialogOpen()
    const isDesktop = useIsDesktop()

    const isMobileDrawer = !isDesktop && mobilePresentation === "drawer"

    const { isOpen, setIsOpen } = useControllableOpenState({ isOpen: controlledOpen, onOpenChange })

    // Layer stack — skip when the mobile BottomDrawer is rendering (it manages its own layer).
    const { overlayZIndex, surfaceZIndex, floatingZIndex } = useLayerStack(isMobileDrawer ? false : isOpen)

    // Registers only while open: a closed popover that mounts must not decrement another popover's count.
    useEffect(() => {
        if (isOpen) {
            setIsAnyDialogOpen(true)
            return () => setIsAnyDialogOpen(false)
        }
    }, [isOpen, setIsAnyDialogOpen])

    const contextValue = useMemo<PopoverContextValues>(
        () => ({
            isOpen,
            isAnchored: Boolean(isAnchored),
            isDismissable,
            hasDragIndicator,
            mobilePresentation,
            setIsOpen,
            overlayZIndex,
            surfaceZIndex,
            floatingZIndex,
        }),
        [
            isOpen,
            isAnchored,
            isDismissable,
            hasDragIndicator,
            mobilePresentation,
            setIsOpen,
            overlayZIndex,
            surfaceZIndex,
            floatingZIndex,
        ],
    )

    if (isMobileDrawer) {
        return (
            <PopoverContext.Provider value={contextValue}>
                <BottomDrawer.Root
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    isDismissable={isDismissable}
                    hasDragIndicator={hasDragIndicator}
                >
                    {children}
                </BottomDrawer.Root>
            </PopoverContext.Provider>
        )
    } else if (!isAnchored) {
        return (
            <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
                <PopoverContext.Provider value={contextValue}>{children}</PopoverContext.Provider>
            </DialogPrimitive.Root>
        )
    } else {
        return (
            <PopoverPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
                <PopoverContext.Provider value={contextValue}>{children}</PopoverContext.Provider>
            </PopoverPrimitive.Root>
        )
    }
}

const Root = Popover
const Header = PopoverSurfaceHeader
const Footer = PopoverFooter
const Body = PopoverBody
const Section = PopoverSection
const View = PopoverView
const Button = PopoverButton

function Trigger(props: ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>) {
    const { isAnchored, mobilePresentation, setIsOpen } = useContext(PopoverContext)
    const isDesktop = useIsDesktop()
    const { children } = props

    const isMobileDrawer = !isDesktop && mobilePresentation === "drawer"

    if (isMobileDrawer) {
        return (
            <span data-radix-popover-trigger="true" style={{ display: "contents" }} onClick={() => setIsOpen(true)}>
                {children}
            </span>
        )
    } else if (!isAnchored) {
        return (
            <DialogPrimitive.Trigger
                {...(props as ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>)}
                asChild={true}
            >
                {children}
            </DialogPrimitive.Trigger>
        )
    } else {
        return (
            <PopoverPrimitive.Trigger {...props} asChild={true}>
                {children}
            </PopoverPrimitive.Trigger>
        )
    }
}

function Close(props: ComponentPropsWithoutRef<typeof PopoverPrimitive.Close>) {
    const { children, asChild, ...rest } = props
    const { isAnchored, mobilePresentation } = useContext(PopoverContext)
    const isDesktop = useIsDesktop()

    const isMobileDrawer = !isDesktop && mobilePresentation === "drawer"

    if (isMobileDrawer) {
        return (
            <BottomDrawer.Close {...(rest as ComponentPropsWithoutRef<typeof BottomDrawer.Close>)} asChild={asChild}>
                {children}
            </BottomDrawer.Close>
        )
    } else if (!isAnchored) {
        return (
            <DialogPrimitive.Close
                {...(rest as ComponentPropsWithoutRef<typeof DialogPrimitive.Close>)}
                asChild={asChild}
            >
                {children}
            </DialogPrimitive.Close>
        )
    } else {
        return (
            <PopoverPrimitive.Close {...rest} asChild={asChild}>
                {children}
            </PopoverPrimitive.Close>
        )
    }
}

function ViewportNavigation(props: PopoverViewportNavigationProps) {
    const { componentIds, children } = props

    // Explicit ids mean the Popover.Views are nested inside a component, where the provider can't discover
    // them from its children; keeping every view mounted is what lets each one manage its own visibility.
    return (
        <ComponentNavigation.Provider
            transitionDurationMS={100}
            componentIds={componentIds}
            keepMounted={componentIds !== undefined}
        >
            <ComponentNavigation.Root>{children}</ComponentNavigation.Root>
        </ComponentNavigation.Provider>
    )
}

function Viewport(props: PopoverViewportProps) {
    const { children, className, style, onEscapeKeyDown, componentIds, ...rest } = props
    const { isAnchored, isDismissable, mobilePresentation, surfaceZIndex, floatingZIndex } = useContext(PopoverContext)
    const isDesktop = useIsDesktop()

    const isMobileDrawer = !isDesktop && mobilePresentation === "drawer"

    const handleEscapeKeyDown = useCallback(
        (event: KeyboardEvent) => {
            onEscapeKeyDown?.(event)
        },
        [onEscapeKeyDown],
    )

    if (isMobileDrawer) {
        return (
            <BottomDrawer.Content className={className} style={style}>
                <ViewportNavigation componentIds={componentIds}>{children}</ViewportNavigation>
            </BottomDrawer.Content>
        )
    } else if (!isAnchored) {
        return (
            <DialogPrimitive.Portal>
                <DialogPrimitive.Content
                    className={clsx(styles.dialog, className)}
                    style={{ zIndex: surfaceZIndex, ...style }}
                    onEscapeKeyDown={handleEscapeKeyDown}
                    onFocusOutside={(event) => {
                        const target = event.target as Element | null
                        if (target?.closest('[role="dialog"], [data-radix-popper-content-wrapper]')) {
                            event.preventDefault()
                        }
                    }}
                    onPointerDownOutside={(event) => {
                        if (!isDismissable) {
                            event.preventDefault()
                            return
                        }
                        const target = event.detail.originalEvent.target ?? event.target
                        const element = target instanceof Element ? target : (target as Node | null)?.parentElement
                        const insideLayer = element?.closest('[role="dialog"], [data-radix-popper-content-wrapper]')
                        if (insideLayer) {
                            event.preventDefault()
                        }
                    }}
                    {...rest}
                >
                    <ViewportNavigation componentIds={componentIds}>{children}</ViewportNavigation>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        )
    } else {
        return (
            <PopoverPrimitive.Portal>
                <PopoverPrimitive.Content
                    className={clsx(className, styles.popover)}
                    style={{ zIndex: floatingZIndex, ...style }}
                    side="bottom"
                    align="end"
                    sideOffset={4}
                    updatePositionStrategy="always"
                    onEscapeKeyDown={handleEscapeKeyDown}
                    onFocusOutside={(event) => {
                        const target = event.target as Element | null
                        if (target?.closest('[role="dialog"]')) {
                            event.preventDefault()
                        }
                    }}
                    onPointerDownOutside={(event) => {
                        const target = event.detail.originalEvent.target ?? event.target
                        const element = target instanceof Element ? target : (target as Node | null)?.parentElement

                        if (element?.closest("[data-radix-popover-trigger]")) {
                            event.preventDefault()
                        }
                    }}
                    {...rest}
                >
                    <ViewportNavigation componentIds={componentIds}>{children}</ViewportNavigation>
                </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
        )
    }
}

function Overlay(props: ComponentPropsWithoutRef<typeof BaseOverlay>) {
    const { isDismissable, setIsOpen, overlayZIndex } = useContext(PopoverContext)
    const { style, onClick: onClickProp, ...rest } = props

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
            if (isDismissable) {
                setIsOpen(false)
            }
            onClickProp?.(event)
        },
        [isDismissable, setIsOpen, onClickProp],
    )

    return <BaseOverlay style={{ zIndex: overlayZIndex, ...style }} onClick={handleClick} {...rest} />
}

export default { Root, Header, Footer, Body, Section, View, Button, Trigger, Close, Viewport, Overlay }
